import React, { useState } from 'react'
import { NavigateFn } from '@reach/router'
import useBuilding from './hooks/useBuilding'
import { Classes, H2 } from '@blueprintjs/core'
import { Dialog } from '@ui'
import { IconButton, Confirmation, Toaster, Details, Flex } from '@ui'
import { ApolloError } from 'apollo-boost'
import { BuildingForm, FormBuilding } from './BuildingForm'
import api, { BMDialogBuilding, UpdateBuildingInput, IHRLocation } from '@api'
import { get } from 'lodash'
// import moment from 'moment'
import track from '@track'
import Event from '@track/Event'
import { useAppData } from '@providers'
import { meridianEnabled } from './utils'
import Permissions from '@permissions/Permissions'
import { PermissionsType, IPermissionsOnBuilding } from '@permissions/types'
import { DateUtil } from '@utils'

interface SimpleTableData {
  label: string
  value: string | React.ReactNode
  optional?: boolean
}

type BuildingDetailsDialogProps = {
  buildingId: string
  navigate: NavigateFn
  reload?: () => void
  openInEdit?: boolean
}
const BuildingDetailsDialog: React.FunctionComponent<BuildingDetailsDialogProps> = ({
  buildingId,
  navigate,
  reload,
  openInEdit = false,
}: BuildingDetailsDialogProps) => {
  const { isLoading, building, update } = useBuilding(buildingId)

  const [dirty, setDirty] = useState(false)
  const [editMode, setEditMode] = useState(openInEdit)
  const { integrations } = useAppData()

  const showIntegrations = integrations && meridianEnabled(integrations.active)

  const onClose = () => navigate && navigate(window.location.pathname)

  function commitUpdate(b: FormBuilding): Promise<BMDialogBuilding> {
    const input: UpdateBuildingInput = {
      id: buildingId,
      name: b.name,
      code: b.code,
      address: b.address,
      buildingTypeId: b.buildingType?.id || null,
      geoLocationId: (b.geoLocation as IHRLocation).id,
      meridianLocation: b.meridianLocation,
      closedAt: b.closedAt ? DateUtil(b.closedAt).format('yyyy-MM-dd') : null,
    }
    return api.building.update(input).then((building: BMDialogBuilding) => {
      reload?.()
      update(building)
      setDirty(false)
      setEditMode(false)
      Toaster.success({ message: 'Building updated' })
    }) as Promise<BMDialogBuilding>
  }

  const mainDetails: SimpleTableData[] = [
    {
      label: 'Address',
      value: get(building, 'address'),
    },
    {
      label: 'Code',
      value: get(building, 'code'),
    },
    {
      label: 'HR Location',
      value: get(building, 'geoLocation.location', '-'),
    },
    {
      label: 'End Date',
      value:
        building && building.closedAt ? DateUtil(building.closedAt).format('MMM d, yyyy') : '-',
    },
    {
      label: 'Building Type',
      value: building?.buildingType?.displayName || '-',
    },
  ]

  const secondaryData: SimpleTableData[] = [
    {
      label: 'Meridian Location External ID',
      value: get(building, 'meridianLocation.externalId') || '-',
    },
  ]

  const canDeleteWithNoFloors = building && !building.floors

  return (
    <Permissions type={PermissionsType.BUILDING} on={building}>
      {(permissions) => {
        const { canUpdate, canDelete } = permissions as IPermissionsOnBuilding

        return editMode ? (
          <Dialog
            isOpen
            style={{ width: 500 }}
            canEscapeKeyClose={false}
            canOutsideClickClose={false}
            transitionDuration={200}
            title="Update Building"
            toolbar={<DialogToolbar onClose={onClose} canDelete={canDeleteWithNoFloors} />}
            isCloseButtonShown={false}
            onClose={onClose}
            body={
              <div className={Classes.DIALOG_BODY}>
                {isLoading || !building ? (
                  <LoadingState
                    mainDetails={mainDetails}
                    secondaryData={secondaryData}
                    showIntegrations={showIntegrations}
                    onClose={onClose}
                  />
                ) : (
                  <BuildingForm
                    showIntegrations={showIntegrations}
                    dirty={dirty}
                    setDirty={setDirty}
                    building={building}
                    onCancel={() => {
                      setDirty(false)
                      setEditMode(false)
                    }}
                    onSubmit={commitUpdate}
                  />
                )}
              </div>
            }
          />
        ) : (
          <Dialog
            isOpen
            style={{ width: 500 }}
            transitionDuration={200}
            canEscapeKeyClose={false}
            isCloseButtonShown={false}
            onClose={onClose}
            toolbar={
              <DialogToolbar
                onEdit={
                  canUpdate
                    ? () => {
                        setEditMode(true)
                        track.buildings({
                          eventName: Event.EDIT_RECORD_DIALOG,
                          props: {
                            component: 'Building Details',
                          },
                        })
                      }
                    : undefined
                }
                onDelete={
                  canDelete
                    ? () => {
                        track.buildings({
                          eventName: Event.DELETE_RECORD_DIALOG,
                          props: {
                            component: 'Building Details',
                          },
                        })
                        return api.building
                          .remove(buildingId as string)
                          .then(() => {
                            if (reload) {
                              reload()
                            }
                            onClose()
                          })
                          .then(() => Toaster.success({ message: 'Building removed' }))
                          .catch((error: ApolloError) => Toaster.fail({ error }))
                      }
                    : undefined
                }
                onClose={onClose}
                canDelete={canDeleteWithNoFloors}
              />
            }
            body={
              <div className={Classes.DIALOG_BODY}>
                {!building || building.id !== buildingId ? (
                  <LoadingState
                    mainDetails={mainDetails}
                    secondaryData={secondaryData}
                    showIntegrations={showIntegrations}
                    onClose={onClose}
                  />
                ) : (
                  <>
                    <Flex direction="column">
                      <H2
                        style={{
                          marginTop: 30,
                          marginRight: 10,
                          marginBottom: 20,
                          wordBreak: 'break-word',
                        }}
                      >
                        {building.name}
                      </H2>
                      {mainDetails.map(({ label, value, optional }, i) => (
                        <Details.Row key={i}>
                          <Details.Label label={label} required={!optional} />
                          {value}
                        </Details.Row>
                      ))}

                      {showIntegrations && (
                        <>
                          <Details.Header label="Integrations Info" iconName="puzzle-piece" />
                          {secondaryData.map(({ label, value, optional }, i) => (
                            <Details.Row key={i}>
                              <Details.Label label={label} required={!optional} />
                              {value}
                            </Details.Row>
                          ))}
                        </>
                      )}
                    </Flex>
                  </>
                )}
              </div>
            }
          ></Dialog>
        )
      }}
    </Permissions>
  )
}

function DialogToolbar({
  onEdit,
  onDelete,
  onClose,
  canDelete,
}: {
  onEdit?: () => void
  onDelete?: () => void
  onClose?: () => void
  canDelete?: boolean
}) {
  return (
    <div className="dialog-buttons">
      {onEdit && (
        <IconButton
          data-button="edit_building"
          minimal
          iconName="pen"
          tooltip="Edit"
          onClick={onEdit}
        />
      )}
      {onDelete &&
        (canDelete ? (
          <Confirmation
            text="Delete this building?"
            onConfirm={onDelete}
            track={() => {
              track.buildings({
                eventName: Event.DELETE_RECORD_DIALOG,
                props: {
                  component: 'Building Details',
                },
              })
            }}
          >
            <IconButton data-button="delete_building" minimal tooltip="Delete" iconName="trash" />
          </Confirmation>
        ) : (
          <IconButton disabled minimal iconName="trash" tooltip="Cannot delete if floors exist" />
        ))}
      {onClose && (
        <IconButton
          data-button="close_dialog"
          minimal
          tooltip="Close"
          iconName="times"
          onClick={onClose}
        />
      )}
    </div>
  )
}

function LoadingState({
  mainDetails,
  secondaryData,
  showIntegrations,
  onClose,
}: {
  mainDetails: SimpleTableData[]
  secondaryData: SimpleTableData[]
  showIntegrations: boolean
  onClose: () => void
}) {
  return (
    <>
      <DialogToolbar onClose={onClose} />
      <Flex direction="column">
        <H2
          className={Classes.SKELETON}
          style={{
            color: 'red',
          }}
        >
          stand in value
        </H2>
        {mainDetails.map(({ label, value, optional }, i) => (
          <Details.Row key={i}>
            <Details.Label label={label} required={!optional} />
            <span className={Classes.SKELETON}>stand in value</span>
          </Details.Row>
        ))}

        {showIntegrations && (
          <>
            <Details.Header label="Integrations Info" iconName="puzzle-piece" />
            {secondaryData.map(({ label, value, optional }, i) => (
              <Details.Row key={i}>
                <Details.Label label={label} required={!optional} />
                <span className={Classes.SKELETON}>stand in value</span>
              </Details.Row>
            ))}
          </>
        )}
      </Flex>
    </>
  )
}

export default BuildingDetailsDialog
